import React from "react";
import { Trans } from "@lingui/macro";
import { Alert } from "components/UI/Alert/Alert";
import { errorGenerator } from "model/system/utils";
import { Loader } from "components/UI/Loader/Loader";
import { Button } from "components/UI/Button/Button";
import { useDispatch, useSelector } from "react-redux";
import { ButtonLink } from "components/UI/ButtonLink/ButtonLink";
import { getFullOrderById } from "model/dashboardOrders/dOrdersAPI";
import { numberCurrencyFormat } from "constants/convertToLocalCurrency";
import { getTransPaymentType } from "../../../../../../constants/paymentType";
import { PopupDetailsSale } from "components/containers/dashboard/dataOrders/popupContents/popupDetailsSale";
import { PopupPanelDashboard } from "components/containers/dashboard/DashboardContentTable/PopupPanelDashboard";
import { OrderedProductsTable } from "components/containers/dashboard/dataOrders/popupContents/popupDetails/popupTables/orderedProductTable";
import { AcceptedProductsTable } from "components/containers/dashboard/dataOrders/popupContents/popupDetails/popupTables/acceptedProductTable";
import { RejectedProductsTable } from "components/containers/dashboard/dataOrders/popupContents/popupDetails/popupTables/rejectedProductTable";
import {
  getColorOrders,
  getTransStatus,
  notEditedStatus,
} from "constants/ordersStatus";
import {
  TagIcon,
  UserIcon,
  LinkIcon,
  ClockIcon,
  BookmarkIcon,
  AnnotationIcon,
  ReceiptTaxIcon,
  LocationMarkerIcon,
  OfficeBuildingIcon,
} from "@heroicons/react/outline";
import {
  IdOrdersState,
  typeOrdersItemFull,
} from "model/dashboardOrders/dOrdersReducer";
import {
  dOrdersSetDetailItemFullAC,
  dOrdersSetSaleDetailItemSagaAC,
} from "model/dashboardOrders/dOrdersActionCreators";

export type typePopupDetails = {
  variant?: string;
  setOpenPopup: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenEditPopup: React.Dispatch<React.SetStateAction<boolean>>;
};

export const PopupDetails: React.FC<typePopupDetails> = ({
  variant,
  setOpenPopup,
  setOpenEditPopup,
}) => {
  const dispatch = useDispatch();

  const dataObjectSlim = useSelector(
    (state: { dOrders: IdOrdersState }) => state.dOrders.detailItem
  );

  const [loader, setLoader] = React.useState(false);
  const [openSale, setOpenSale] = React.useState<boolean>(false);
  const [dataObject, setDataObject] = React.useState<
    typeOrdersItemFull | undefined
  >(undefined);

  React.useEffect(() => {
    if (dataObjectSlim) {
      setLoader(true);
      const setData = async () => {
        const res = await getFullOrderById({ orderId: dataObjectSlim.id });
        if (res.status === 200) {
          setDataObject(res.data);
        } else {
          errorGenerator(res);
        }
        setLoader(false);
      };
      setData().then();
    }
  }, [dataObjectSlim]);

  const onEditClick = () => {
    dispatch(dOrdersSetDetailItemFullAC(dataObject));
    setOpenPopup(false);
    setOpenEditPopup(true);
  };

  const onSaleClick = () => {
    dispatch(dOrdersSetSaleDetailItemSagaAC(dataObject!.saleId!));
    setOpenSale(true);
  };

  return (
    <>
      {loader && <Loader />}
      {dataObjectSlim ? (
        <>
          <div className="grid gap-x-8 grid-cols-1 sm:grid-cols-4 border-b sm:border sm:rounded-md sm:px-4">
            <div className="flex  flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-t-0 sm:border-b ">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <BookmarkIcon className="h-5 w-5 text-gray-500" />
                <Trans>Orders number</Trans>:
              </div>
              <div className="flex  self-center text-gray-90 text-left">
                {dataObject?.number || dataObjectSlim.number || "-"}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2 sm:col-span-2 sm:py-4 border-t border-b sm:border-t-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <OfficeBuildingIcon className="h-5 w-5 text-gray-500" />
                <Trans>Store:</Trans>
              </div>
              <div className="flex  self-center text-gray-90 text-left ">
                {dataObject?.store.name || dataObjectSlim.store?.name || "-"}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2  sm:py-4 border-b ">
              <ClockIcon className="h-5 w-5 text-gray-500" />
              <div className="text-gray-500 text-left">
                <Trans>Creation date</Trans>:
              </div>
              <div className="text-gray-90 text-left">
                {(dataObject?.dateCreated || dataObjectSlim?.dateCreated || "-")
                  .substring(0, 16)
                  .replace("T", ", ")}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-t-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <LinkIcon className="h-5 w-5 text-gray-500" />
                <Trans>External order number</Trans>:
              </div>
              <div
                title={
                  dataObject?.externalId || dataObjectSlim.externalId || "-"
                }
                className="text-gray-90 text-left overflow-hidden break-words"
              >
                {dataObject?.externalId || dataObjectSlim.externalId || "-"}
              </div>
            </div>
            <div className="flex  flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
              <ReceiptTaxIcon className="h-5 w-5 text-gray-500" />
              <div className="text-gray-500 text-left">
                <Trans>Payments</Trans>:
              </div>
              <div className="divide-y">
                {!!dataObject?.payments.length ? (
                  dataObject.payments.map((item) => {
                    const date = new Date(item.date)
                      .toISOString()
                      .substring(0, 16)
                      .replace("T", ", ");
                    return (
                      <div key={item.id}>
                        <div className={"flex flex-wrap gap-x-2 "}>
                          <span className={"text-gray-500"}>
                            <Trans>data</Trans>
                          </span>{" "}
                          : <span className={"text-gray-90"}>{date}</span>
                        </div>
                        <div className={"flex flex-wrap gap-x-2  text-left"}>
                          <span className={"text-gray-500"}>
                            <Trans>amount</Trans>
                          </span>{" "}
                          :{" "}
                          <span className={"text-gray-90"}>
                            {numberCurrencyFormat(item.amount)}
                          </span>
                          <span className={"text-gray-90"}>
                            ({" "}
                            {item.method
                              ? getTransPaymentType(item.method)
                              : " - "}{" "}
                            )
                          </span>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <span className={"text-gray-500"}>
                    <Trans>The order not yet paid.</Trans>
                  </span>
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2  sm:py-4 border-b sm:border-b-0">
              <LinkIcon className="h-5 w-5 text-gray-500" />
              <div className="text-gray-500 text-left">
                <Trans>Sale</Trans>:
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.saleId ? (
                  <ButtonLink
                    type={"button"}
                    label={<Trans>Sale's details</Trans>}
                    onClick={onSaleClick}
                  />
                ) : (
                  "-"
                )}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2 border-t sm:py-4 sm:border-0">
              <TagIcon className="h-5 w-5 text-gray-500 self-start" />
              <div className="flex  self-start text-gray-500 text-left">
                <Trans>Order's status</Trans>:
              </div>
              <div className="flex  self-start text-gray-90 text-left">
                <span
                  className={` bg-${getColorOrders(
                    dataObject?.status || dataObjectSlim.status
                  )} rounded w-2 h-2 mr-2 self-center`}
                >
                  {" "}
                </span>
                <span>
                  {getTransStatus(
                    dataObject?.status || dataObjectSlim.status || "-"
                  )}
                </span>
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <UserIcon className="h-5 w-5 text-gray-500" />
                <Trans>Client name</Trans>:
              </div>
              <div className="flex  self-center text-gray-90 text-left font-medium">
                {dataObject?.customerName || dataObjectSlim.customerName || "-"}
              </div>
            </div>
            <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <UserIcon className="h-5 w-5 text-gray-500" />
                <Trans>Client phone</Trans>:
              </div>
              <div className="flex  self-center text-gray-90 text-left font-medium">
                {dataObject?.phone || dataObjectSlim?.phone || "-"}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:col-span-2 sm:py-4">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <LocationMarkerIcon className="h-5 w-5 text-gray-500" />
                <Trans>Destination address</Trans>:
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.address || dataObjectSlim?.address || "-"}
              </div>
            </div>
            <div className="sm:col-span-2 flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 sm:py-4">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <AnnotationIcon className="h-5 w-5 text-gray-500" />
                <Trans>Comment</Trans>:
              </div>
              <div className="text-gray-90 text-left">
                {dataObject?.comment || dataObjectSlim?.comment || "-"}
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 sm:py-4 border-t border-b sm:border-t-0 sm:border-b-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Creator by</Trans>:
              </div>
              <div className="flex text-gray-90 text-left items-center">
                {dataObject?.userCreated?.name ||
                  dataObjectSlim?.userCreated?.name ||
                  "-"}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 sm:py-4">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Courier:</Trans>
              </div>
              <div className="flex text-gray-90 text-left items-center">
                {dataObject?.courier?.name ||
                  dataObjectSlim.courier?.name ||
                  "-"}
              </div>
            </div>
          </div>

          <div className="mt-4 sm:mt-2.5 grid gap-x-8 grid-cols-1 sm:grid-cols-2 sm:border sm:rounded-md sm:px-4">
            <div className="flex flex-wrap gap-x-4 py-2 border-t border-b  sm:py-4 sm:border-t-0 sm:border-b-0">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Edited by</Trans>:
              </div>
              <div className="text-gray-90 text-left ">
                {dataObject?.userUpdated?.name ||
                  dataObjectSlim?.userUpdated?.name ||
                  "-"}
              </div>
            </div>

            <div className="flex flex-wrap gap-x-4 py-2 border-b sm:border-b-0 sm:py-4">
              <div className="flex gap-x-2 items-center text-gray-500 text-left">
                <Trans>Date</Trans>:
              </div>
              <div className="flex self-center text-gray-90 text-left">
                {(dataObject?.dateUpdated || dataObjectSlim.dateUpdated || "-")
                  .substring(0, 16)
                  .replace("T", ", ")}
              </div>
            </div>
          </div>

          {!!dataObject?.orderedProducts.length && (
            <OrderedProductsTable dataObject={dataObject} variant={variant} />
          )}

          {!!dataObject?.acceptedProducts.length && (
            <AcceptedProductsTable dataObject={dataObject} variant={variant} />
          )}

          {!!dataObject?.rejectedProducts.length && (
            <RejectedProductsTable dataObject={dataObject} variant={variant} />
          )}

          <div className="flex gap-4 mt-5 sm:mt-4 sm:flex sm:col-span-2 justify-end items-end w-full ">
            <Alert
              className={"flex-grow"}
              type={"info"}
              title={
                <Trans>
                  You cannot edit an order after the order has been delivered to
                  the customer.
                  <br />
                  Orders in the status "DELIVERED", "CANCELLED" or "COMPLETED"
                  are available for viewing only.
                </Trans>
              }
            />
            {dataObject && !notEditedStatus.includes(dataObject.status) && (
              <Button
                type="button"
                label={<Trans>Edit</Trans>}
                onClick={() => onEditClick()}
                variant={variant}
                disabled={notEditedStatus.includes(dataObject!.status)}
              />
            )}
            <Button
              type="button"
              secondary={true}
              label={<Trans>Close</Trans>}
              onClick={() => setOpenPopup(false)}
              variant={variant}
            />
          </div>
        </>
      ) : (
        <Alert type="error" title={<Trans>Have no data.</Trans>} />
      )}
      {/* Popup Details Sale dialog */}
      <PopupPanelDashboard
        title={<Trans>Sale's details</Trans>}
        openPopup={openSale}
        setOpenPopup={setOpenSale}
      >
        <PopupDetailsSale
          crossLinkToRefund={false}
          setOpenPopup={setOpenSale}
        />
      </PopupPanelDashboard>
    </>
  );
};
